<template>
  <b-modal
    no-close-on-backdrop
    centered
    ok-only
    size="xl"
    ok-title="Fechar"
    ok-variant="danger"
    v-model="open"
    v-if="open"
    :title="`${fields.selectedProductId} - Preços do produto: ${productName}`"
    :busy="loading"
  >
    <b-row v-if="loading">
      <b-col cols="12" class="text-center">
        <b-spinner label="Spinning"></b-spinner>
      </b-col>
    </b-row>
    <b-row v-else>
      <b-col
        cols="12"
        class="d-flex align-items-center justify-content-between mb-4"
      >
        <span>Preços customizados por estado</span>
        <b-button
          variant="success"
          title="Adicionar novo preço para o produto"
          @click="addNew()"
        >
          <i class="fas fa-save mr-2"></i>
          Adicionar novo
        </b-button>
      </b-col>
      <b-col cols="12">
        <b-table
          striped
          responsive
          hover
          bordered
          show-empty
          :fields="table.headers"
          :items="table.records"
          :busy="loading"
          empty-text="Sem preços customizados para esse produto"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-2"></b-spinner>
              <strong>Carregando...</strong>
            </div>
          </template>
          <template #cell(state)="data">
            <div class="d-flex align-items-center">
              <span class="fs-12 font-weight-bold mr-2">{{
                data.index + 1
              }}</span>
              <SelectStates
                style="width: 220px"
                :exclude-selected-values="false"
                :already-selected-values="table.records.map((x) => x.state)"
                v-model="table.records[`${data.index}`].state"
                propValueName="uf"
                :disabled="!data.item.editing"
                @update:value="(requiredFields[data.index] || {}).state = ''"
              />
            </div>
            <error-message
              :errorMessage="(requiredFields[data.index] || {}).state"
            />
          </template>
          <template #cell(userType)="data">
            <SelectUserTypes
              :disabled="!data.item.editing"
              :loading="loading"
              v-model="table.records[`${data.index}`].userType"
            />
          </template>
          <template #cell(price)="data">
            <InputMoney
              v-if="data.item.editing"
              v-model="table.records[`${data.index}`].price"
            />
            <span v-else>{{
              (data.item.price || '').includes('R$')
                ? data.item.price
                : formatNumber(data.item.price)
            }}</span>
          </template>
          <template #cell(actions)="data">
            <b-col class="pr-0">
              <div v-if="data.item.editing" class="d-inline-block">
                <b-button
                  variant="success"
                  size="sm"
                  title="Salvar"
                  class="mr-2 mb-2"
                  :disabled="loadingRows[data.index]"
                  @click="save({ index: data.index })"
                >
                  <i class="fas fa-save"></i>
                </b-button>
                <b-button
                  variant="warning"
                  size="sm"
                  title="Cancelar edição"
                  class="mr-2 mb-2"
                  :disabled="loadingRows[data.index]"
                  @click="cancelPriceEdit({ index: data.index })"
                >
                  <i class="fas fa-ban"></i>
                </b-button>
              </div>
              <b-button
                v-if="!data.item.editing"
                variant="primary"
                size="sm"
                title="Editar preço"
                class="mb-2 mr-2"
                :disabled="loadingRows[data.index]"
                @click="
                  editPrice({
                    id: data.item.id,
                  })
                "
              >
                <i class="fas fa-edit"></i>
              </b-button>
              <b-button
                variant="danger"
                size="sm"
                title="Remover produto"
                class="mb-2"
                :disabled="loadingRows[data.index]"
                @click="
                  deleteProductPrice({
                    index: data.index,
                    id: data.item.id,
                    state: data.item.state,
                  })
                "
              >
                <i class="fas fa-trash"></i>
              </b-button>

              <b-spinner
                small
                v-if="loadingRows[data.index]"
                class="align-middle ml-2"
              ></b-spinner>
            </b-col>
          </template>
        </b-table>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { formatNumber } from '@/utils';

import makeToast from '@/mixins/makeToast';

import InputMoney from 'Components/Inputs/InputMoney.vue';
import SelectUserTypes from 'Components/Inputs/InputUserTypes.vue';
import SelectStates from 'Components/Inputs/SelectStates.vue';
import ErrorMessage from 'Components/ErrorMessage.vue';

export default {
  name: 'SetSelectedProductPrice',
  emits: ['saved'],
  mixins: [makeToast],
  data() {
    return {
      loading: false,
      loadingRows: {},
      open: false,
      productName: '',
      fields: this.defaultPrice({}),
      table: {
        headers: [
          {
            key: 'index',
            thClass: 'd-none',
            tdClass: 'd-none',
          },
          {
            key: 'id',
            thClass: 'd-none',
            tdClass: 'd-none',
          },
          {
            key: 'editing',
            thClass: 'd-none',
            tdClass: 'd-none',
          },
          {
            key: 'state',
            label: 'Estado',
            sortable: false,
          },
          {
            key: 'userType',
            label: 'Tipo de Usuário',
            tdClass: 'w-25',
            sortable: false,
            thStyle: 'min-width: 160px;'
          },
          {
            key: 'price',
            label: 'Preço',
            sortable: false,
            thClass: 'text-center',
            tdClass: 'text-center',
            thStyle: 'min-width: 140px;'
          },
          {
            key: 'updatedAt',
            label: 'Atualizado em',
            sortable: false,
            thClass: 'text-center',
            tdClass: 'text-center',
            thStyle: 'min-width: 150px;'
          },
          {
            key: 'actions',
            label: 'Ações',
            sortable: false,
            thClass: 'text-center',
            tdClass: 'text-center',
            thStyle: 'min-width: 160px;'
          },
        ],
        records: [],
      },
      requiredFields: {},
    };
  },
  computed: {
    ...mapState(['dadosUsuario']),
    ...mapGetters('shopkeeper', ['getUserTypesEnabled']),
  },
  mounted() {
    if (!this.getUserTypesEnabled) {
      this.$store.dispatch(`shopkeeper/clientesCadastro`, this.dadosUsuario.id);
    }
  },
  methods: {
    formatNumber,
    ...mapActions('shopkeeper', [
      'saveSelectedProductPrice',
      'getSelectedProductPrices',
      'deleteSelectedProductPrice',
    ]),
    toggleOpen({
      selectedProductId = 0,
      productName = '',
      productId = 0,
      alreadyCreatedPrices = [],
    }) {
      this.clear();

      this.open = !this.open;
      this.productName = productName;
      this.fields.selectedProductId = selectedProductId || '';
      this.fields.productId = productId || 0;

      if (alreadyCreatedPrices && alreadyCreatedPrices.length > 0) {
        this.table.records = alreadyCreatedPrices;
      }

      this.get();
    },
    get() {
      if (this.loading || !this.fields.selectedProductId) return;

      this.loading = true;

      this.getSelectedProductPrices({
        construpUserId: this.fields.construpUserId,
        selectedProductId: this.fields.selectedProductId,
      })
        .then((response) => {
          const { data } = response.data.data;
          this.table.records = data || [];
        })
        .catch(() => {
          this.makeToast(
            'Erro',
            'Houve um erro ao trazer os preços do produto',
            'danger'
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save({ index }) {
      if(!this.table.records[index].state){
        this.makeToast(
          'Erro',
          'Favor selecionar o estado',
          'danger'
          );
        return
      }
      
      if(!this.table.records[index].userType){
        this.makeToast(
          'Erro',
          'Favor selecionar o tipo de usuário',
          'danger'
          );
        return
      }

      let price = parseFloat((this.table.records[index].price.replace('R$', '')).replace(',','.'));

      if(price <= 0){
        this.makeToast(
          'Erro',
          'O preço tem que ser maior que R$ 0,00',
          'danger'
          );
        return
      }

      if (!this.fields.selectedProductId) {
        this.$emit('saved', {
          ...(this.table.records[index] || {}),
          productId: this.fields.productId,
          editing: false,
        });
        this.table.records[index].editing = false;
        return;
      }

      if (this.loading || index <= -1) return;

      this.$set(this.loadingRows, index, true);

      const request = {
        ...(this.table.records[index] || {}),
        selectedProductId: this.fields.selectedProductId,
        productId: this.fields.productId,
        construpUserId: (this.dadosUsuario || {}).id,
      };

      this.saveSelectedProductPrice(request)
        .then((response) => {
          const { id } = response.data.data || {};
          this.makeToast('Sucesso', 'Preço do produto salvo com sucesso');

          this.table.records[index].editing = false;
          this.table.records[index].id = id;
          this.table.records[index].updatedAt = new Date().toLocaleString(
            'pt-BR',
            {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            }
          );
        })
        .catch((error) => {
          this.handleErrorMessages(index, error.response.data.message);
          this.makeToast(
            'Erro',
            `Houve um erro ao salvar o preço do produto na linha ${
              index + 1
            }: ${error.response.data.message}`,
            'danger'
          );
        })
        .finally(() => {
          this.$set(this.loadingRows, index, false);
        });
    },
    deleteProductPrice({ index, id, state }) {
      if (!id) {
        this.$emit('delete', {
          ...(this.table.records[index] || {}),
          productId: this.fields.productId,
        });
        this.$delete(this.table.records, index, 1);
        return;
      }

      this.$set(this.loadingRows, index, true);

      this.deleteSelectedProductPrice({
        id,
        construpUserId: this.dadosUsuario.id,
        selectedProductId: this.fields.selectedProductId,
        state,
      })
        .then(() => {
          this.makeToast('Sucesso', 'Preço excluído com sucesso');

          this.get();
        })
        .catch(() => {
          this.makeToast(
            'Erro',
            `Houve um erro ao excluir o preço do estado ${state}`,
            'danger'
          );
        })
        .finally(() => {
          this.$set(this.loadingRows, index, false);
        });
    },
    editPrice({ id }) {
      const price = this.table.records.find((x) => x.id === id);
      const index = this.table.records.indexOf(price);

      this.$set(this.table.records[`${index}`], 'editing', true);
    },
    cancelPriceEdit({ index }) {
      this.$set(this.table.records[`${index}`], 'editing', false);
    },
    clear() {
      this.productName = '';
      this.fields = this.defaultPrice({});
      this.table.records = [];
    },
    defaultPrice({ editing = false }) {
      return {
        id: '',
        construpUserId: (this.dadosUsuario || {}).id,
        selectedProductId: 0,
        productId: 0,
        price: '',
        state: '',
        status: '',
        userType: '',
        editing,
      };
    },
    addNew() {
      this.table.records.push(
        this.defaultPrice({
          editing: true,
        })
      );
      this.$forceUpdate();
    },
    handleErrorMessages(index, errorMessages) {
      if (!errorMessages) return;

      this.$set(this.requiredFields, index, {});

      Object.keys(errorMessages).forEach((key) => {
        this.$set(this.requiredFields[index], key, 'Campo obrigatório');
      });
    },
  },
  components: {
    InputMoney,
    SelectStates,
    ErrorMessage,
    SelectUserTypes,
  },
};
</script>

<style scoped>
>>> .vs__selected-options .vs__selected {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;
}
</style>